import React from "react";

import { FormControlLabel, Radio as MuiRadio } from "@mui/material";
import type { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import Button from "@readai/common/components/Button";
import { roundedOutline } from "@readai/common/components/styles";
import ArrowLeft from "@readai/icons/uui/ArrowLeft";
import IconButton from "@readai/pulse/components/IconButton";
import Stack, { Row, StackProps } from "@readai/pulse/components/Stack";

import { Card } from "@app/booking/components/styles";
import { RecommendedTimeIndicator } from "@app/booking/components/styles";

export const Header = styled<React.FC<StackProps>>((props) => (
  <Stack variant="center" {...props} />
))(({ theme }) => ({
  backgroundColor: "#FFF",
  paddingTop: theme.spacing(3),
  width: "100%",
  "@media (min-height: 700px)": {
    paddingTop: theme.spacing(5),
  },
}));

export const Heading = styled<React.FC<TypographyProps>>((props) => (
  <Typography variant="h2" {...props} />
))({
  fontWeight: 500,
});

export const TabsContainer = styled(Stack)(({ theme }) => ({
  width: "350px",
  maxWidth: "100%",
  flexShrink: 1,
  padding: theme.spacing(0, 1.5),
  borderBottom: `2px solid ${theme.palette.shades.neutral[50]}`,
  "& .MuiTabs-root": { marginBottom: "-2px" },
}));

export const BackButton = styled<React.FC<IconButtonProps>>((props) => (
  <IconButton {...props} size="small">
    <ArrowLeft />
  </IconButton>
))(({ theme }) => ({
  "& svg": {
    color: theme.palette.shades.neutral[300],
  },
}));

export const IndicatorLegend = styled((props: StackProps) => (
  <Stack variant="center" {...props}>
    <RecommendedTimeIndicator />
  </Stack>
))(({ theme }) => ({
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.shades.meadow[50],
  borderRadius: "50%",
}));

export const AddGuestButton = styled(Button)(({ theme }) => ({
  height: "25px",
  color: theme.palette.shades.secondary[500],
}));

export const OuterUserPanel = styled(Stack)(({ theme }) => ({
  background: theme.palette.gradients.light,
  padding: theme.spacing(2),
  [theme.breakpoints.up("lg")]: {
    padding: 0,
    backgroundColor: theme.palette.background.default,
  },
}));

export const UserPanel = styled(Stack)(({ theme }) => ({
  backgroundColor: "#FFF",
  borderRadius: "8px",
  padding: theme.spacing(3),
  width: "100%",
  [theme.breakpoints.up("lg")]: {
    borderRadius: 0,
    backgroundColor: theme.palette.background.default,
    width: "317px",
    minWidth: "317px",
    maxWidth: "317px",
    flex: 1,
  },
}));

export const ContentWrapper = styled(Stack)(({ theme }) => ({
  height: "100%",
  width: "100%",
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(5),
  },
}));

export const ContentCard = styled(Card)(({ theme }) => ({
  flexDirection: "column",
  minWidth: "100%",
  width: "100%",
  maxWidth: "100%",
  height: "100%",
  maxHeight: "100%",

  [theme.breakpoints.down("lg")]: {
    borderRadius: 0,
  },

  [theme.breakpoints.up("lg")]: {
    flexDirection: "row",
    minWidth: "1000px",
    maxWidth: "1000px",
    height: "800px",
  },
}));

export const ContentContainer = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: "#FFF",
  width: "100%",
  position: "relative",
  overflowY: "auto",
  [theme.breakpoints.down("lg")]: {
    width: "100vw",
  },
}));

export const Content = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  width: "100%",
  maxWidth: "100%",
  flexGrow: 1,
  overflowY: "auto",
  height: "100%",
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up("lg")]: {
    height: "calc(100% - 80px)", // height of the footer
    marginBottom: "80px", // height of the footer
    paddingBottom: 0,
  },
}));

export const FooterContainer = styled((props: StackProps) => (
  <Stack variant="center" {...props} />
))(({ theme }) => ({
  height: "80px",
  backgroundColor: "#FFF",
  width: "100%",
  borderTop: `solid 1px ${theme.palette.shades.neutral[50]}`,
  position: "absolute",
  bottom: 0,

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

export const ActionRow = styled(Row)(({ theme }) => ({
  width: "fit-content",
  [theme.breakpoints.up("lg")]: {
    width: "100%",
  },
}));

export const Footer = ({ children }: { children: React.ReactNode }) => (
  <FooterContainer>
    <ActionRow variant="center" padding={2} spacing={1}>
      {children}
    </ActionRow>
  </FooterContainer>
);

export const RadioControlLabel = styled(FormControlLabel)(
  ({ theme, checked }) => ({
    border: `solid 1px ${theme.palette.shades.neutral[100]}`,
    position: "relative",
    marginLeft: 0,
    minWidth: "275px",
    borderRadius: "6px",
    "& .MuiButtonBase-root": {
      padding: "3px",
    },
    "& .MuiFormControlLabel-label": {
      fontWeight: 500,
      marginLeft: theme.spacing(1),
    },
    marginRight: 0,
    padding: theme.spacing(1),
    ...(checked && {
      borderColor: theme.palette.shades.primary[500],
      backgroundColor: theme.palette.shades.ghost[500],
      ...roundedOutline({
        borderRadius: "5px",
        border: `1px solid ${theme.palette.shades.primary[500]}`,
      }),
    }),
  }),
);

export const Radio = styled(MuiRadio)(({ theme }) => ({
  "&.Mui-checked": {
    color: theme.palette.shades.primary[500],
  },
  color: theme.palette.shades.neutral[200],
}));

export const LoggedInUserContentContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.shades.ghost[500],
  borderRadius: theme.corners.small,
  color: theme.palette.shades.neutral[500],
  border: "1px solid",
  borderColor: theme.palette.transparent.black[8],
}));
